import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "../style/CareerPage.module.css";
import { FaBriefcase, FaUsers, FaRocket, FaTimes, FaCheckCircle, FaMapMarkerAlt, FaMoneyBillWave } from "react-icons/fa";
import { useGetCareerQuery } from "../redux/api";
import SEO from "../components/SEO";

const CareerPage = () => {
  const { data } = useGetCareerQuery();
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    resumeLink: "",
    coverLetter: ""
  });

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setSuccess(false);
    setFormData({ name: "", email: "", phone: "", resumeLink: "", coverLetter: "" });
  };

  const closeModal = () => {
    setSelectedJob(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || "https://api.createchsquads.com") + "/api";
      const res = await fetch(`${baseUrl}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobId: selectedJob._id,
          jobTitle: selectedJob.jobTitle,
          ...formData
        })
      });

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => closeModal(), 2000);
      } else {
        alert("Failed to apply. Please try again.");
      }
    } catch (err) {
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.careerPage}>
      <SEO
        title="IT & Digital Marketing Careers"
        description="Explore exciting career opportunities at CreaTech Squads. Join our team of 18 experts in SEO, development, design, and digital marketing in India."
        keywords="CreaTech Squads careers, digital marketing jobs India, web developer jobs Kanpur, SEO jobs, IT company hiring"
        canonical="/careers"
      />

      {/* HERO */}
      <motion.section
        className={styles.hero}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className={styles.heroContent}>
          <h1>Join Our <span className={styles.highlight}>Team</span></h1>
          <p>Build your future with us in a dynamic and creative environment.</p>
        </div>
      </motion.section>

      {/* STATS */}
      <motion.section className={styles.stats}>
        <div className={styles.statsGrid}>
          <motion.div whileHover={{ translateY: -10 }} className={styles.statCard}>
            <FaUsers className={styles.icon} />
            <h3>18</h3>
            <span>Team Members</span>
          </motion.div>

          <motion.div whileHover={{ translateY: -10 }} className={styles.statCard}>
            <FaBriefcase className={styles.icon} />
            <h3>95+</h3>
            <span>Projects Completed</span>
          </motion.div>

          <motion.div whileHover={{ translateY: -10 }} className={styles.statCard}>
            <FaRocket className={styles.icon} />
            <h3>9+</h3>
            <span>Years Experience</span>
          </motion.div>
        </div>
      </motion.section>

      {/* JOB LIST */}
      <section className={styles.jobsSection}>
        <motion.div className={styles.jobsHeader}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Open Positions</h2>
          <p>Find the role that best fits your skills and aspirations.</p>
        </motion.div>

        <div className={styles.jobsGrid}>
          {data?.map((job, i) => (
            <motion.div
              key={job._id}
              className={styles.jobCard}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className={styles.jobHeader}>
                <h3>{job.jobTitle}</h3>
                <span className={styles.jobType}>Full-Time</span>
              </div>

              <p className={styles.jobDesc}>{job.description}</p>

              <div className={styles.jobMeta}>
                <div className={styles.metaItem}><span><FaMapMarkerAlt /></span> {job.location}</div>
                <div className={styles.metaItem}><span><FaBriefcase /></span> {job.experience} Years</div>
                <div className={styles.metaItem}><span><FaMoneyBillWave /></span> {job.salary}</div>
              </div>

              <button className={styles.applyBtnBtn} onClick={() => handleApplyClick(job)}>Apply Now</button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <motion.section
        className={styles.cta}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className={styles.ctaContainer}>
          <h2>Ready to Start Your Career?</h2>
          <p>If you don&apos;t see a matching role, send us your resume anyway!</p>
          <a href="mailto:Info@createchsquads.com" className={styles.ctaBtn}>Email Your Resume</a>
        </div>
      </motion.section>

      {/* MODAL */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div 
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className={styles.modalContent}
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              <button className={styles.closeBtn} onClick={closeModal}><FaTimes /></button>
              
              {success ? (
                <div className={styles.successMessage}>
                  <FaCheckCircle className={styles.successIcon} />
                  <h3>Application Submitted!</h3>
                  <p>We will review your profile and get back to you soon.</p>
                </div>
              ) : (
                <>
                  <h3 className={styles.modalTitle}>Apply for: {selectedJob.jobTitle}</h3>
                  <form onSubmit={handleSubmit} className={styles.applyForm}>
                    <div className={styles.formGroup}>
                      <label>Full Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="John Doe" />
                    </div>
                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label>Email Address *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="john@example.com" />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Phone Number *</label>
                        <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="+91 XXXXX XXXXX" />
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <label>Resume / Portfolio Link *</label>
                      <input type="url" name="resumeLink" value={formData.resumeLink} onChange={handleInputChange} required placeholder="https://drive.google.com/..." />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Cover Letter (Optional)</label>
                      <textarea name="coverLetter" value={formData.coverLetter} onChange={handleInputChange} rows="4" placeholder="Tell us why you are a great fit..."></textarea>
                    </div>
                    
                    <button type="submit" className={styles.submitBtn} disabled={loading}>
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default CareerPage;
